import React, { useState } from 'react';
    import { useDonation } from '../context/DonationContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiEdit2, FiCheck, FiPlus, FiSearch, FiTag } = FiIcons;

    function FMVManager() {
      const { state, dispatch } = useDonation();
      const [search, setSearch] = useState('');
      const [editingKey, setEditingKey] = useState(null); // 'name-category'
      const [newType, setNewType] = useState({ name: '', category: 'Women', low: 5, high: 20 });
      const [showAdd, setShowAdd] = useState(false);

      // Get unique categories from current state to populate the dropdown
      const availableCategories = [...new Set(state.fmvItems.map(item => item.category))].sort();

      const filteredItems = state.fmvItems.filter(item => 
        item.name.toLowerCase().includes(search.toLowerCase()) || 
        item.category.toLowerCase().includes(search.toLowerCase())
      ).sort((a, b) => a.name.localeCompare(b.name));

      const handleUpdate = (item, field, value) => {
        dispatch({
          type: 'UPDATE_FMV',
          payload: {
            name: item.name,
            category: item.category,
            updates: { [field]: parseFloat(value) || 0 }
          }
        });
      };

      const handleAdd = (e) => {
        e.preventDefault();
        dispatch({ type: 'ADD_ITEM_TYPE', payload: newType });
        setNewType({ name: '', category: 'Women', low: 5, high: 20 });
        setShowAdd(false);
      };

      return (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col h-full">
          <div className="p-4 border-b border-gray-100 bg-gray-50/50 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center">
                <SafeIcon icon={FiTag} className="mr-2 text-blue-500" />
                Fair Market Values
              </h3>
              <button 
                onClick={() => setShowAdd(!showAdd)}
                className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg font-bold hover:bg-blue-700 transition-colors flex items-center"
              >
                <SafeIcon icon={FiPlus} className="mr-1" />
                Add Type
              </button>
            </div>
            
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search item types..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              />
            </div>
          </div>

          {showAdd && (
            <form onSubmit={handleAdd} className="p-4 bg-blue-50 border-b border-blue-100 animate-in slide-in-from-top-2 duration-300 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input 
                  placeholder="Item Name" 
                  className="col-span-2 px-3 py-2 rounded-lg border border-blue-200 outline-none text-sm"
                  value={newType.name}
                  onChange={(e) => setNewType(p => ({ ...p, name: e.target.value }))}
                  required
                />
                <select 
                  className="px-3 py-2 rounded-lg border border-blue-200 text-sm"
                  value={newType.category}
                  onChange={(e) => setNewType(p => ({ ...p, category: e.target.value }))}
                >
                  {availableCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <div className="flex space-x-2">
                  <input 
                    type="number" 
                    placeholder="Low" 
                    className="w-full px-3 py-2 rounded-lg border border-blue-200 text-sm"
                    value={newType.low}
                    onChange={(e) => setNewType(p => ({ ...p, low: e.target.value }))}
                  />
                  <input 
                    type="number" 
                    placeholder="High" 
                    className="w-full px-3 py-2 rounded-lg border border-blue-200 text-sm"
                    value={newType.high}
                    onChange={(e) => setNewType(p => ({ ...p, high: e.target.value }))}
                  />
                </div>
              </div>
              <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-bold">
                Create New Item Type
              </button>
            </form>
          )}

          <div className="flex-1 overflow-y-auto custom-scrollbar">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-white border-b border-gray-100 text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                <tr>
                  <th className="px-4 py-2">Item Type</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2 text-right">Low/High</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredItems.map((item) => {
                  const key = `${item.name}-${item.category}`;
                  const isEditing = editingKey === key;
                  return (
                    <tr key={key} className="group hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-700">{item.name}</td>
                      <td className="px-4 py-3 text-gray-500">{item.category}</td>
                      <td className="px-4 py-3 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end space-x-2">
                            <input 
                              type="number" 
                              className="w-12 px-1 border border-blue-200 rounded text-right"
                              defaultValue={item.low}
                              onBlur={(e) => handleUpdate(item, 'low', e.target.value)}
                            />
                            <span className="text-gray-300">-</span>
                            <input 
                              type="number" 
                              className="w-12 px-1 border border-blue-200 rounded text-right"
                              defaultValue={item.high}
                              onBlur={(e) => handleUpdate(item, 'high', e.target.value)}
                            />
                            <button onClick={() => setEditingKey(null)} className="text-green-500">
                              <SafeIcon icon={FiCheck} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end space-x-2">
                            <span className="text-gray-600 font-bold">${item.low}-${item.high}</span>
                            <button 
                              onClick={() => setEditingKey(key)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-blue-500 hover:bg-blue-50 rounded transition-all"
                            >
                              <SafeIcon icon={FiEdit2} className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    export default FMVManager;